import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Event, nip19 } from 'nostr-tools';
import { useNostrProfile } from '../../hooks/useNostrProfile';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { UserAvatar } from '../Common/UserAvatar';
import { Heart, MessageSquare, Repeat, Zap } from 'lucide-react';

interface NotificationItemProps {
    event: Event;
}



const NotificationItem: React.FC<NotificationItemProps> = ({ event }) => {
    const { metadata } = useNostrProfile(event.pubkey);
    const displayName = metadata?.display_name || metadata?.name || 'Anonim';
    const npub = nip19.npubEncode(event.pubkey);
    const navigate = useNavigate();

    const getType = () => {
        switch (event.kind) {
            case 1:
                return event.tags.some(t => t[0] === 'e') ? 'reply' : 'mention';
            case 6: return 'repost';
            case 7: return 'like';
            case 9735: return 'zap';
            default: return 'unknown';
        }
    };

    const type = getType();

    // Zap Amount Logic
    const getZapAmount = () => {
        if (type !== 'zap') return null;

        // Try to find 'amount' tag (millisats)
        const amountTag = event.tags.find(t => t[0] === 'amount');
        if (amountTag && amountTag[1]) {
            return Math.floor(parseInt(amountTag[1]) / 1000); // Convert millisats to sats
        }

        // Fallback: Check bolt11 invoice (less reliable without heavy decoding lib, but we can try)
        // Usually index 1 of 'bolt11' tag
        const bolt11Tag = event.tags.find(t => t[0] === 'bolt11');
        if (bolt11Tag) {
            // We won't implement full bolt11 decoding here to keep it light.
            // Rely on 'amount' tag which is standard for NIP-57 zaps.
            return null;
        }
        return null;
    };

    const zapAmount = getZapAmount();

    const getActionText = () => {
        switch (type) {
            case 'like': return 'senin bir entryni beğendi';
            case 'zap': return 'sana zap gönderdi!';
            case 'reply': return 'sana cevap verdi.';
            case 'mention': return 'senden bahsetti.';
            case 'repost': return 'entryni alıntıladı.';
            default: return 'etkileşimde bulundu.';
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'like': return <Heart className="w-5 h-5 text-red-500 fill-current" />;
            case 'zap': return <Zap className="w-5 h-5 text-orange-500 fill-current" />;
            case 'reply': return <MessageSquare className="w-5 h-5 text-mavi-500 fill-current" />;
            case 'mention': return <MessageSquare className="w-5 h-5 text-green-500 fill-current" />;
            case 'repost': return <Repeat className="w-5 h-5 text-green-500" />;
            default: return null;
        }
    };

    const targetEventId = event.tags.find(t => t[0] === 'e')?.[1];

    const targetLink = type === 'mention' || !targetEventId || type === 'zap'
        ? `/entry/${event.id}`
        : `/entry/${targetEventId}`;

    const finalLink = type === 'zap' && targetEventId
        ? `/entry/${targetEventId}`
        : targetLink;

    const handleRowClick = (e: React.MouseEvent) => {
        // Navigate unless clicked on interactive elements
        if ((e.target as HTMLElement).closest('a') || (e.target as HTMLElement).closest('button')) {
            return;
        }
        navigate(finalLink);
    };

    return (
        <div
            onClick={handleRowClick}
            className="block hover:bg-gray-50 dark:hover:bg-surface-800/50 transition-colors p-4 border-b border-gray-100 dark:border-surface-800 cursor-pointer"
        >
            <div className="flex items-start gap-3">
                <div className="shrink-0 relative z-10">
                    <Link to={`/profile/${npub}`} onClick={(e) => e.stopPropagation()}>
                        <UserAvatar
                            src={metadata?.picture}
                            name={displayName}
                            size="md"
                            isExternal={false}
                        />
                    </Link>
                </div>

                <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 dark:text-gray-100 font-poppins">
                        <Link
                            to={`/profile/${npub}`}
                            className="font-bold hover:underline hover:text-mavi-600 dark:hover:text-mavi-400 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {displayName}
                        </Link>
                        {' '}
                        <span className="text-gray-600 dark:text-gray-400">
                            {getActionText()}
                        </span>
                    </div>

                    {(type === 'reply' || type === 'mention') && event.content && (
                        <div className="mt-1 text-gray-600 dark:text-gray-400 text-sm line-clamp-2 font-merriweather bg-surface-50 dark:bg-surface-900 p-2 rounded-lg border border-surface-100 dark:border-surface-800">
                            "{event.content}"
                        </div>
                    )}

                    {type === 'zap' && (
                        <div className="mt-1 flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 font-bold font-poppins bg-orange-50 dark:bg-orange-900/10 px-2 py-1 rounded-md w-fit">
                            <Zap className="w-3 h-3 fill-orange-500 text-orange-500" />
                            {zapAmount ? `${zapAmount.toLocaleString()} sats` : 'Zap!'}
                        </div>
                    )}

                    <time className="text-xs text-gray-400 mt-2 block font-poppins">
                        {formatDistanceToNow(event.created_at * 1000, { addSuffix: true, locale: tr })}
                    </time>
                </div>

                <div className="shrink-0 pt-1 opacity-70">
                    {getIcon()}
                </div>
            </div>
        </div>
    );
};

export default NotificationItem;
